'use client';

import {Globe} from 'lucide-react';
import React, {useState} from 'react';
import {usePathname, useRouter} from '#i18n/navigation.ts';
import {useParams, useSearchParams} from 'next/navigation';
import LanguagePickerPopupMenu from './LanguagePickerPopupMenu';
import {Button} from '@purpose/ui/components/button';
import {cn} from '@purpose/ui/lib/utils';
import {useUnsavedChanges} from '@/src/contexts/UnsavedChangesContext';
import UnsavedChangesModal from '../UnsavedChangesModal';
import {checkUserRole} from '@/src/utils/cookies';
import {useUser} from '@/src/contexts/UserContext';
import {
  useUpdateMerchantLanguage,
  useUpdateOrganisationLanguage,
  useUpdateUserLanguage
} from '@/src/hooks/queries';
import {USER_ROLES} from '@/src/utils/constants';
import {LOCALE_CODES} from '@/src/i18n/constants';

function LanguagePicker({
  inversion = false,
  rightMenu = false,
  bottomMenu = false,
  buttonClassName,
  iconClassName
}: {
  inversion?: boolean;
  rightMenu?: boolean;
  bottomMenu?: boolean;
  buttonClassName?: string;
  iconClassName?: string;
}) {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const {locale} = useParams<{locale: string}>();
  const router = useRouter();
  const pathname = usePathname();

  const cookiesRole = checkUserRole();
  const {userRole} = useUser();
  const role = cookiesRole || userRole;

  const [anchorEl, setAnchorEl] = useState(null);
  const [newLocale, setNewLocale] = useState<string | null>(null);

  const {link, setLink, open, setOpen} = useUnsavedChanges();

  const userLanguageMutation = useUpdateUserLanguage();
  const organisationLanguageMutation = useUpdateOrganisationLanguage();
  const merchantLanguageMutation = useUpdateMerchantLanguage();

  function handleClose() {
    setAnchorEl(null);
  }

  function changeLanguage(lngCode: string) {
    handleClose();
    if (newLocale || lngCode) {
      router.push(
        {
          pathname,
          query
        },
        {locale: newLocale ?? lngCode}
      );
      if (role) {
        switch (role) {
          case USER_ROLES.member:
            userLanguageMutation.mutateAsync(
              (newLocale ?? lngCode) as LOCALE_CODES
            );
            break;
          case USER_ROLES.organisation:
            organisationLanguageMutation.mutateAsync(
              (newLocale ?? lngCode) as LOCALE_CODES
            );
            break;
          case USER_ROLES.merchant:
            merchantLanguageMutation.mutateAsync(
              (newLocale ?? lngCode) as LOCALE_CODES
            );
            break;
        }
      }
    }
    setNewLocale(null);
    setLink(undefined);
  }

  function handleClick(event) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleChangeLocale(lngCode: string) {
    if (link) {
      handleClose();
      setNewLocale(lngCode);
      setOpen(true);
    } else {
      changeLanguage(lngCode);
    }
  }

  return (
    <div>
      <UnsavedChangesModal
        link={link}
        setLink={setLink}
        open={open}
        setOpen={setOpen}
        onSave={changeLanguage as () => void}
      />
      <Button
        variant="ghost"
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        className={cn(
          `${inversion && 'hover:bg-black/20 active:bg-black/20'} cursor-pointer touch-manipulation`,
          buttonClassName
        )}
        aria-label="Endre språk"
      >
        <Globe
          className={cn(
            inversion ? 'text-white' : 'text-primary-80',
            iconClassName
          )}
        />
      </Button>

      <LanguagePickerPopupMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        rightMenu={rightMenu}
        bottomMenu={bottomMenu}
        locale={locale}
        onChange={handleChangeLocale}
      />
    </div>
  );
}

export default LanguagePicker;
